import { statementsSharingAMonth } from './accountTimelines'
import { consecutiveRuns, monthsBetween } from './months'
import {
  isFailedRead,
  missingStatementReason,
  twoStatementsReasons,
} from './statementReasons'
import type {
  AccountMonthStatus,
  AccountStatement,
  AccountTimeline,
  Badge,
  BadgeReason,
  MonthKey,
  VerifyingStatement,
} from './types'

// One account's statements looked up by month, built once per data change.
export interface AccountIndex {
  timeline: AccountTimeline
  // Only months with exactly one statement. A month with two keeps neither value.
  statementByMonth: Map<MonthKey, AccountStatement>
  sharedMonths: Map<MonthKey, AccountStatement[]>
  // Statements the customer uploaded whose securities could not be read. They carry no value.
  failedReadByMonth: Map<MonthKey, AccountStatement>
  coveredMonths: Set<MonthKey>
  verifyingByMonth: Map<MonthKey, VerifyingStatement>
  firstMonth: MonthKey | null
  lastMonth: MonthKey | null
  lastStatement: AccountStatement | null
}

const earliest = (months: MonthKey[]) =>
  months.length === 0
    ? null
    : months.reduce((first, month) => (month < first ? month : first))

const latest = (months: MonthKey[]) =>
  months.length === 0
    ? null
    : months.reduce((last, month) => (month > last ? month : last))

export const indexAccount = (timeline: AccountTimeline): AccountIndex => {
  const sharedMonths = new Map(
    statementsSharingAMonth(timeline).map((group) => [group[0].month, group]),
  )
  const statementByMonth = new Map<MonthKey, AccountStatement>()
  const failedReadByMonth = new Map<MonthKey, AccountStatement>()
  const coveredMonths = new Set<MonthKey>()
  for (const statement of timeline.statements) {
    if (!sharedMonths.has(statement.month)) {
      const byMonth = isFailedRead(statement)
        ? failedReadByMonth
        : statementByMonth
      byMonth.set(statement.month, statement)
    }
    for (const month of statement.coveredMonths) coveredMonths.add(month)
  }
  const verifyingByMonth = new Map<MonthKey, VerifyingStatement>()
  for (const statement of timeline.verifying) {
    for (const month of statement.coveredMonths)
      verifyingByMonth.set(month, statement)
  }
  const allMonths = [...coveredMonths, ...verifyingByMonth.keys()]
  return {
    timeline,
    statementByMonth,
    sharedMonths,
    failedReadByMonth,
    coveredMonths,
    verifyingByMonth,
    firstMonth: earliest(allMonths),
    lastMonth: latest(allMonths),
    lastStatement: timeline.statements[timeline.statements.length - 1] ?? null,
  }
}

// Only a printed $0.00 total proves the account was emptied. A $0.00 added up from no rows does not.
export const closedAtPrintedZero = (statement: AccountStatement | null) =>
  statement?.printedHoldingsTotal === 0 && statement.value === 0

// The months after a last statement that printed a $0.00 total are not missing.
const hasEnded = (index: AccountIndex, month: MonthKey) =>
  index.lastMonth != null &&
  month > index.lastMonth &&
  closedAtPrintedZero(index.lastStatement)

export const accountMonthStatus = (
  index: AccountIndex,
  month: MonthKey,
): AccountMonthStatus => {
  if (index.sharedMonths.has(month)) return 'two_statements'
  if (index.statementByMonth.has(month)) return 'statement'
  if (index.failedReadByMonth.has(month)) return 'failed_read'
  if (index.verifyingByMonth.has(month)) return 'verifying'
  if (index.firstMonth == null || month < index.firstMonth) {
    return 'before_first_statement'
  }
  if (index.coveredMonths.has(month)) return 'mid_cycle'
  if (hasEnded(index, month)) return 'ended'
  return 'missing'
}

// Before its first statement an account has not started: it is not missing, and it sits out the
// month's total and count. We cannot prove when it opened, only when its statements start.
export const isNotStarted = (status: AccountMonthStatus) =>
  status === 'before_first_statement'

export const missingMonthsUpTo = (
  index: AccountIndex,
  month: MonthKey,
): MonthKey[] =>
  index.firstMonth == null
    ? []
    : monthsBetween(index.firstMonth, month).filter(
        (candidate) => accountMonthStatus(index, candidate) === 'missing',
      )

const statementsUpTo = (index: AccountIndex, month: MonthKey) =>
  index.timeline.statements.filter((statement) => statement.month <= month)

const sharedMonthReasons = (index: AccountIndex, month: MonthKey) =>
  Array.from(index.sharedMonths)
    .filter(([shared]) => shared <= month)
    .reverse()
    .flatMap(([shared, statements]) => twoStatementsReasons(shared, statements))

// The As-of month's own problem leads, since the row shows only the first reason (Cesar's 0239 hid its
// failed May check behind older gaps). Then missing months, then each statement's reasons, newest first.
export const accountBadge = (index: AccountIndex, month: MonthKey): Badge => {
  const missingReasons = consecutiveRuns(missingMonthsUpTo(index, month))
    .map(missingStatementReason)
    .reverse()
  const statementReasons: BadgeReason[] = statementsUpTo(index, month)
    .reverse()
    .flatMap((statement) => statement.reasons)
  const newestFirst = [
    ...sharedMonthReasons(index, month),
    ...missingReasons,
    ...statementReasons,
  ]
  const reasons = [
    ...newestFirst.filter((reason) => reason.month === month),
    ...newestFirst.filter((reason) => reason.month !== month),
  ]
  if (reasons.length > 0) return { status: 'needs_review', reasons }
  const isVerifying = index.timeline.verifying.some(
    (statement) => statement.month <= month,
  )
  return { status: isVerifying ? 'verifying' : 'verified', reasons: [] }
}
